// A person or group as a list row: avatar, name, a secondary line, optional trailing buttons, and
// the row's gesture actions (swipe, long-press menu, "more" button). It carries one aria-label so
// a screen reader hears one coherent row, and the label is text the caller already wrote.
import { Avatar, Column, ListRow } from "../ui";
import type { AvatarPresence, AvatarSize, RowAction } from "../ui";

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

export interface PersonRowProps {
  /** Person or group name. */
  title: string;
  /** Role, status, presence or member count: text, never colour alone. */
  subtitle?: string | undefined;
  /** A second secondary line (a group's description). */
  description?: string | undefined;
  initials: string;
  avatarUrl?: string | undefined;
  avatarSize?: AvatarSize;
  presence?: AvatarPresence | undefined;
  /** Buttons at the end of the row. */
  trailing?: React.ReactNode;
  testId?: string | undefined;
  onPress?: (() => void) | undefined;
  /** Everything one can do to the row: the long-press menu and the "more" button list them. */
  actions?: readonly RowAction[] | undefined;
  swipeStart?: RowAction | undefined;
  swipeEnd?: RowAction | undefined;
}

export function PersonRow({
  title,
  subtitle,
  description,
  initials,
  avatarUrl,
  avatarSize = "row",
  presence,
  trailing,
  testId,
  onPress,
  actions,
  swipeStart,
  swipeEnd,
}: PersonRowProps): React.JSX.Element {
  const second = [subtitle, description].filter((part) => part !== undefined && part !== "");
  const label = [title, ...second].join(", ");
  return (
    <Column
      role="group"
      aria-label={label}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(testId === undefined ? {} : { "data-testid": testId })}
    >
      <ListRow
        title={title}
        subtitle={second.length === 0 ? undefined : second.join(" · ")}
        leading={
          <Avatar initials={initials} src={avatarUrl} size={avatarSize} presence={presence} />
        }
        controls={trailing}
        onPress={onPress}
        actions={actions}
        swipeStart={swipeStart}
        swipeEnd={swipeEnd}
      />
    </Column>
  );
}
